import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LEARN } from "../../theme";
import { GLYPH_STROKES } from "../../../../data/glyphStrokes";
import { worldContainingLetter } from "../../../../utils/learnLetterProgress";
import GlyphTracer from "../../interaction/GlyphTracer";
import { strokesFromPoints } from "../../interaction/glyphGeometry";
import { useCompleteOnce } from "./engineUtils";

/**
 * Guided trace: the child traces big A, then little a, on the canvas tracer.
 * Strokes come from GLYPH_STROKES; the legacy config.cases.*.strokes point
 * arrays are only a fallback for a glyph the catalogue does not cover.
 */
function buildCases(letter, config) {
  const upper = String(letter || "").toUpperCase();
  const lower = String(letter || "").toLowerCase();
  const legacy = config?.cases || {};
  return [
    { key: "uppercase", glyph: upper, label: `Big ${upper}` },
    { key: "lowercase", glyph: lower, label: `little ${lower}` },
  ]
    .map((c) => {
      const entry = GLYPH_STROKES[c.glyph];
      if (entry?.strokes?.length) return { ...c, strokes: entry.strokes, hint: entry.hint };
      const old = legacy[c.key];
      const strokes = strokesFromPoints(old?.strokes, old?.prompts);
      return strokes.length ? { ...c, strokes, hint: old?.introSpeech || null } : null;
    })
    .filter(Boolean);
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

export default function GuidedTraceGame({ letter, config, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const cases = useMemo(() => buildCases(letter, config), [letter, config]);
  const world = worldContainingLetter(letter);
  const theme = LEARN.world[world?.id] || { accent: LEARN.color.blue, deep: LEARN.color.blueDeep };
  const [caseIndex, setCaseIndex] = useState(0);
  const [doneKeys, setDoneKeys] = useState(() => new Set());
  const current = cases[caseIndex];
  const advanceTimer = useRef(null);
  useEffect(() => () => clearTimeout(advanceTimer.current), []);

  // Say the case hint ("Big A: slide down, slide down, little line across").
  // The player speaks the activity prompt ~350ms after mount, so the first
  // hint queues behind it; later cases interrupt.
  useEffect(() => {
    if (!current?.hint) return undefined;
    if (caseIndex === 0) {
      const t = setTimeout(() => speak?.(current.hint, { dedupe: true }), 1200);
      return () => clearTimeout(t);
    }
    speak?.(current.hint, { interrupt: true });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseIndex, current?.glyph]);

  const handleCaseComplete = useCallback(() => {
    if (!current) return;
    setDoneKeys((prev) => new Set(prev).add(current.key));
    if (caseIndex + 1 < cases.length) {
      advanceTimer.current = setTimeout(() => setCaseIndex(caseIndex + 1), reduceMotion ? 0 : 420);
    } else {
      finish();
    }
  }, [caseIndex, cases.length, current, finish, reduceMotion]);

  if (!current) {
    return (
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ margin: 0, textAlign: "center", color: LEARN.color.ink, fontWeight: 700, fontFamily: LEARN.font, background: "rgba(255,255,255,0.92)", padding: "14px 20px", borderRadius: LEARN.radius.md }}>
          No trace paths for this letter yet.
        </p>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column", fontFamily: LEARN.font }}>
      <div aria-label="Letter case" style={{ flexShrink: 0, display: "flex", justifyContent: "center", gap: 8, paddingBottom: 10 }}>
        {cases.map((c, i) => {
          const active = i === caseIndex;
          const done = doneKeys.has(c.key);
          return (
            <div
              key={c.key}
              aria-current={active ? "step" : undefined}
              aria-label={`${c.label}${done ? ", done" : active ? ", tracing now" : ""}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, minHeight: 40, padding: "6px 16px",
                borderRadius: LEARN.radius.pill, fontWeight: 700, fontSize: 18,
                background: active ? theme.accent : "rgba(255,255,255,0.9)",
                color: active ? "#fff" : done ? theme.accent : LEARN.color.muted,
                border: `2px solid ${active ? theme.accent : LEARN.color.line}`,
                boxShadow: active ? LEARN.shadow.sticker(theme.deep) : LEARN.shadow.card,
                transition: reduceMotion ? "none" : `all ${LEARN.motion.base}ms ${LEARN.motion.spring}`,
                transform: active && !reduceMotion ? "translateY(-1px)" : "none",
              }}
            >
              {done && <CheckIcon />}
              <span>{c.label}</span>
            </div>
          );
        })}
      </div>
      <GlyphTracer
        key={current.key}
        glyph={current.glyph}
        strokes={current.strokes}
        label={`Trace ${current.label}`}
        accent={theme.accent}
        accentDeep={theme.deep}
        speak={speak}
        sfx={sfx}
        onWrong={onWrong}
        onComplete={handleCaseComplete}
        reduceMotion={reduceMotion}
      />
    </div>
  );
}
